import React from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import './UlltraLegacy.sass'

const IMAGES = '/assets/images/ulltra-legacy'
const ULLTRA_HREF = 'https://ulltra.ai'

// El aviso se muestra una sola vez por sesión.
const SESSION_KEY = 'ulltra-aviso-cerrado'

// Salida del aviso antes de desmontarlo. Por encima de su transition de .35s.
const NOTICE_EXIT = 380

// La web NO hace scroll en la ventana: su CSS pone
// .scrollableLayout{position:absolute;top:0;bottom:0;overflow-y:auto} y html a
// height:100%. El scroll ocurre dentro de ese contenedor, así que es ahí donde
// va el scroll-padding-top. Si cambia esa estructura, es lo primero que falla.
const SCROLL_SELECTOR = '.scrollableLayout'

const RESERVED_VAR = '--ulltra-barra'
const PINNED_ATTR = 'data-ulltra-fijo'
const MUTATION_DELAY = 250

// sessionStorage puede estar bloqueado (Safari privado, políticas
// corporativas). Si lo está, el aviso sale en cada carga: ese es el fallo
// aceptable, nunca una excepción.
const readFlag = () => {
  try {
    return window.sessionStorage.getItem(SESSION_KEY) === '1'
  } catch (e) {
    return false
  }
}

const writeFlag = () => {
  try {
    window.sessionStorage.setItem(SESSION_KEY, '1')
    return true
  } catch (e) {
    return false
  }
}

const getScroller = () =>
  document.querySelector(SCROLL_SELECTOR) || document.documentElement

/**
 * Aviso "Glue Digital ahora es Ulltra": cabecera permanente —no se puede
 * cerrar, es permanente por diseño— y aviso de bienvenida a pantalla completa
 * una vez por sesión.
 *
 * Se monta en un portal a document.body a propósito: las dos piezas son
 * position:fixed y cualquier ancestro con transform, filter o perspective las
 * anclaría a él en vez de a la ventana.
 */
export class UlltraLegacy extends React.Component {
  bar = React.createRef()

  notice = React.createRef()

  dismissButton = React.createRef()

  pinned = [] // elementos fijos de la web que hemos desplazado

  closing = false

  previousFocus = null

  previousOverflow = ''

  constructor (props) {
    super(props)
    this.state = {
      bar: false, // la barra ha entrado
      notice: false, // el aviso está en el DOM
      noticeOn: false // el aviso está visible
    }
  }

  componentDidMount = () => {
    const { withNotice } = this.props

    // La barra aparece al cerrar el aviso: ahí es cuando el visitante pasa a la
    // web antigua y tiene que estar ya puesta. Si el aviso no se muestra —ya se
    // cerró en esta sesión, o está desactivado— sale sola al cargar.
    if (!withNotice || readFlag()) {
      this.showBar()
    } else {
      this.openNotice()
    }
  }

  componentWillUnmount = () => {
    window.removeEventListener('load', this.applySpace)
    window.removeEventListener('resize', this.applySpace)
    document.removeEventListener('keydown', this.handleKeyDown)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.mutationObserver) this.mutationObserver.disconnect()
    clearTimeout(this.mutationTimer)
    clearTimeout(this.exitTimer)
    cancelAnimationFrame(this.raf1)
    cancelAnimationFrame(this.raf2)
    this.releaseNotice()
    this.restorePinned()
  }

  handleDismiss = () => {
    if (this.closing) return
    this.closing = true

    const { onNoticeDismiss } = this.props

    writeFlag()
    this.releaseNotice()
    this.setState({ noticeOn: false })
    this.showBar() // la barra, ya visible al salir
    if (onNoticeDismiss) onNoticeDismiss()

    this.exitTimer = setTimeout(() => this.setState({ notice: false }), NOTICE_EXIT)
  }

  handleKeyDown = (event) => {
    if (event.key === 'Escape' || event.keyCode === 27) this.handleDismiss()
  }

  /* ── Aviso ─────────────────────────────────────────────────────────── */

  openNotice = () => {
    this.previousFocus = document.activeElement
    this.previousOverflow = document.documentElement.style.overflow
    document.documentElement.style.overflow = 'hidden'
    document.addEventListener('keydown', this.handleKeyDown)

    this.setState({ notice: true }, () => {
      if (this.dismissButton.current) this.dismissButton.current.focus()

      // Doble rAF: si la clase que dispara la transición se añade en el mismo
      // frame en que el elemento entra en el DOM, el navegador no calcula el
      // estado inicial y no hay transición, solo un salto.
      this.raf1 = requestAnimationFrame(() => {
        this.raf2 = requestAnimationFrame(() => this.setState({ noticeOn: true }))
      })
    })
  }

  releaseNotice = () => {
    document.removeEventListener('keydown', this.handleKeyDown)
    document.documentElement.style.overflow = this.previousOverflow
    if (this.previousFocus && document.body.contains(this.previousFocus)) {
      this.previousFocus.focus()
    }
    this.previousFocus = null
  }

  /* ── Barra ─────────────────────────────────────────────────────────── */

  showBar = () => {
    const { bar } = this.state
    if (bar) return

    this.setState({ bar: true }, () => {
      this.applySpace()
      window.addEventListener('load', this.applySpace)
      window.addEventListener('resize', this.applySpace)

      if (window.ResizeObserver && this.bar.current) {
        this.resizeObserver = new window.ResizeObserver(this.applySpace)
        this.resizeObserver.observe(this.bar.current)
      }

      // Es una SPA: al navegar no recarga, cambia el DOM, y aparecen elementos
      // fijos nuevos.
      if (window.MutationObserver) {
        this.mutationObserver = new window.MutationObserver(() => {
          clearTimeout(this.mutationTimer)
          this.mutationTimer = setTimeout(this.applySpace, MUTATION_DELAY)
        })
        this.mutationObserver.observe(document.body, { childList: true, subtree: true })
      }
    })
  }

  /**
   * Hace sitio a la barra sin tocar el marcado ni las hojas de estilo de la
   * web. El contenido NO se empuja: la barra flota por encima. Empujarlo
   * dejaba a la vista una franja del lienzo del navegador arriba y abajo de la
   * barra —15px por lado— que se leía como un corte de otro color. Lo que sí
   * se hace es bajar los elementos fijos y ajustar el scroll-padding-top, para
   * que los enlaces de ancla no queden ocultos debajo.
   */
  applySpace = () => {
    const bar = this.bar.current
    if (!bar) return

    // offsetHeight y no getBoundingClientRect: al llamarse, la barra todavía
    // puede estar desplazada hacia arriba por el transform de entrada, así que
    // su rect no vale. Se reserva separación + alto + separación.
    const gap = parseFloat(window.getComputedStyle(bar).top) || 0
    const reserved = gap + bar.offsetHeight + gap

    document.documentElement.style.setProperty(RESERVED_VAR, `${reserved}px`)
    getScroller().style.scrollPaddingTop = `${reserved}px`

    this.pinned = this.pinned
      .filter((item) => document.body.contains(item.el))
      .concat(this.scanPinned(reserved))

    this.pinned.forEach((item) => {
      const node = item.el
      if (!document.body.contains(node)) return
      const real = item.base + item.corr // dónde caía de verdad
      const top = reserved + Math.max(0, real) - item.corr
      node.style.top = `${top}px`
    })
  }

  /**
   * Busca los elementos anclados arriba que la barra taparía.
   *
   * Los fixed y sticky ignoran cualquier padding del flujo, así que hay que
   * bajarlos uno a uno. Se detectan por comportamiento y no por selector, así
   * que sigue funcionando si cambian de nombre: caen aquí la cabecera "Hey
   * Bot", la de páginas internas, la chapa de FONDOS UE y la de Workshops
   * —esta última en top:8px, por eso el umbral no puede ser "top <= 0" sino
   * "top por debajo del alto reservado"—. Las capas a pantalla completa (menú
   * hamburguesa, fondo de los modales) quedan fuera a propósito: deben seguir
   * cubriendo la pantalla.
   */
  scanPinned = (reserved) => {
    const found = []
    const ignore = [this.bar.current, this.notice.current].filter(Boolean)
    const all = Array.prototype.slice.call(document.body.querySelectorAll('*'))

    all.forEach((el) => {
      if (el.hasAttribute(PINNED_ATTR)) return
      if (ignore.some((root) => root === el || root.contains(el))) return

      const cs = window.getComputedStyle(el)
      if (cs.position !== 'fixed' && cs.position !== 'sticky') return

      const base = parseFloat(cs.top)
      if (Number.isNaN(base) || base >= reserved) return

      const rect = el.getBoundingClientRect()
      if (cs.position === 'fixed' && rect.height >= window.innerHeight * 0.9) return

      el.setAttribute(PINNED_ATTR, '1')
      found.push({
        el,
        base, // top declarado en su CSS
        // Desfase entre el top declarado y dónde acaba de verdad. La chapa de
        // FONDOS UE, por ejemplo, declara top:0 pero lleva un
        // transform:translateY(-27px) del propio diseño para colgar del borde:
        // sin esto, al bajarla se quedaba pisando la barra. En sticky no se
        // mide, porque su caja depende del scroll.
        corr: cs.position === 'fixed' ? rect.top - base : 0,
        originalTop: el.style.top
      })
    })

    return found
  }

  restorePinned = () => {
    this.pinned.forEach((item) => {
      const node = item.el
      if (!document.body.contains(node)) return
      node.style.top = item.originalTop
      node.removeAttribute(PINNED_ATTR)
    })
    this.pinned = []
    getScroller().style.scrollPaddingTop = ''
    document.documentElement.style.removeProperty(RESERVED_VAR)
  }

  /* ── Render ────────────────────────────────────────────────────────── */

  renderBar = () => {
    const { href } = this.props
    const { bar } = this.state

    return (
      <header
        id="ulltra-barra"
        ref={this.bar}
        role="banner"
        className={bar ? 'ub-on' : ''}
        aria-hidden={bar ? null : true}
      >
        <div className="ub-arte" aria-hidden="true" />
        <div className="ub-in">
          <div className="ub-lado ub-izq">
            <span className="ub-tag">
              <FormattedMessage id="ulltra.bar.tag" />
            </span>
          </div>
          <p className="ub-txt">
            <FormattedMessage
              id="ulltra.bar.text"
              values={{
                glue: <strong>Glue Digital</strong>,
                brand: <em>Ulltra</em>
              }}
            />
          </p>
          <div className="ub-lado ub-der">
            <a className="ub-cta" href={href} tabIndex={bar ? null : -1}>
              <FormattedMessage id="ulltra.bar.cta" />
            </a>
          </div>
        </div>
      </header>
    )
  }

  renderNotice = () => {
    const { href } = this.props
    const { noticeOn } = this.state

    return (
      <div
        id="ulltra-aviso"
        ref={this.notice}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ulltra-aviso-titulo"
        className={noticeOn ? 'ua-on' : ''}
      >
        <div className="ua-panel">
          <img className="ua-hero" alt="" aria-hidden="true" src={`${IMAGES}/aviso-hero.jpg`} />
          <img className="ua-logo" alt="Ulltra" src={`${IMAGES}/ulltra-logo.png`} />
          <div className="ua-inner">
            <p className="ua-label">
              <FormattedMessage id="ulltra.notice.eyebrow" />
            </p>
            <h2 className="ua-title" id="ulltra-aviso-titulo">
              <FormattedMessage
                id="ulltra.notice.title"
                values={{ br: <br />, brand: <em>Ulltra</em> }}
              />
            </h2>
            <p className="ua-body">
              <FormattedMessage id="ulltra.notice.body" />
            </p>
            <div className="ua-actions">
              <a className="ua-cta" href={href}>
                <FormattedMessage id="ulltra.notice.cta" />
              </a>
              <button
                className="ua-legacy"
                type="button"
                ref={this.dismissButton}
                onClick={this.handleDismiss}
              >
                <FormattedMessage id="ulltra.notice.dismiss" />
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  render = () => {
    const { notice } = this.state

    if (typeof document === 'undefined') return null

    return ReactDOM.createPortal(
      <div className="ulltra-legacy">
        {this.renderBar()}
        {notice ? this.renderNotice() : null}
      </div>,
      document.body
    )
  }
}

UlltraLegacy.propTypes = {
  /** Destino de los dos botones. */
  href: PropTypes.string,
  /** A false, solo sale la barra: sin aviso de bienvenida. */
  withNotice: PropTypes.bool,
  /** Se llama al cerrar el aviso, por si quieres medirlo. */
  onNoticeDismiss: PropTypes.func
}

UlltraLegacy.defaultProps = {
  href: ULLTRA_HREF,
  withNotice: true,
  onNoticeDismiss: null
}

export default UlltraLegacy
